import { useForm, useWatch } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";

import { toast } from "@luce/ui";

import type { SubscriptionCancellationReasonEnum } from "../../__generated__/graphql";
import { useCancelSubscription } from "../gqlhooks/use-cancel-subscription";
import type { CancelSubscriptionProps } from "./cancel-subscription.types";

type UseCancelSubscriptionFormArgs = Pick<CancelSubscriptionProps, "onSuccess" | "onTrack">;

const cancelSchema = z.object({
  reason: z.string().min(1, { error: "Please select a reason" }),
  additional: z.string(),
});

type CancelFormValues = z.infer<typeof cancelSchema>;

const emptyValues: CancelFormValues = { reason: "", additional: "" };

export function useCancelSubscriptionForm({ onSuccess, onTrack }: UseCancelSubscriptionFormArgs) {
  const form = useForm<CancelFormValues>({
    mode: "onChange",
    resolver: zodResolver(cancelSchema),
    defaultValues: emptyValues,
  });

  const { cancelSubscription, loading: isSubmitting } = useCancelSubscription();

  const onSubmit = form.handleSubmit(async (values) => {
    const reason = values.reason as SubscriptionCancellationReasonEnum;
    const detail = values.additional.trim() || undefined;

    const result = await cancelSubscription({ reason, detail });

    if (result.success) {
      onTrack({
        name: "plus_cancel_complete",
        values: {
          reason: reason,
          reason_detail: String(detail),
        },
      });
      form.reset(emptyValues);
      onSuccess();
      return;
    }

    toast.error(result.error ?? "Couldn't cancel your subscription. Please try again.");
  });

  const reset = () => form.reset(emptyValues);

  const reason = useWatch({ control: form.control, name: "reason" });

  return { form, isSubmitting, hasReason: Boolean(reason), onSubmit, reset };
}
